import { tweenPromise, tweenWaitPromise } from '@Animation/utils'
import { KENO_NUMBER_STATUS, KenoNumber } from '../base/number'
import { NEON_LIGHT_TEXTURE_KEYS, NEON_LIGHTS_ATLAS_FRAMES } from '.'
import { STANDARD_LARGE_NUMBER_RATIO } from '@Animation/constants'
import { ANIMATION_IDENTIFIERS } from '@Animation/types'

export class NeonLightsNumber extends KenoNumber {
  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    number: number,
    isPlayerSelected: boolean,
  ) {
    super(scene, x, y, number, isPlayerSelected, ANIMATION_IDENTIFIERS.NEON_LIGHTS)
  }

  getGlowAtlasFrame() {
    return NEON_LIGHTS_ATLAS_FRAMES.NUMBER_GLOW
  }

  getGlowAtlas() {
    return NEON_LIGHT_TEXTURE_KEYS.ATLAS
  }

  createStatusTextureMap(): { [key in KENO_NUMBER_STATUS]: string } {
    return {
      [KENO_NUMBER_STATUS.DEFAULT]: NEON_LIGHT_TEXTURE_KEYS.NUMBER_DEFAULT,
      [KENO_NUMBER_STATUS.SELECTED_PLAYER]: NEON_LIGHT_TEXTURE_KEYS.NUMBER_PLAYER_SELECTED,
      [KENO_NUMBER_STATUS.SELECTED_SYSTEM]: NEON_LIGHT_TEXTURE_KEYS.NUMBER_SYSTEM_SELECTED,
      [KENO_NUMBER_STATUS.SELECTED_HIT]: NEON_LIGHT_TEXTURE_KEYS.NUMBER_HIT,
    }
  }

  async transitionToSystemSelected(): Promise<void> {
    const originalPosition = new Phaser.Math.Vector2(this.x, this.y)
    const newPosition = this.calculatePopPositionInFrame()

    for (let flicker = 1; flicker <= 3; flicker++) {
      this.image.setTexture(NEON_LIGHT_TEXTURE_KEYS.NUMBER_SYSTEM_SELECTED, this.number - 1)
      await tweenWaitPromise(this.scene, 100)()
      this.image.setTexture(NEON_LIGHT_TEXTURE_KEYS.NUMBER_DEFAULT, this.number - 1)
      await tweenWaitPromise(this.scene, 400)()
    }
    this.image.setTexture(NEON_LIGHT_TEXTURE_KEYS.NUMBER_SYSTEM_SELECTED, this.number - 1)
    await tweenWaitPromise(this.scene, 100)()
    this.image.setTexture(NEON_LIGHT_TEXTURE_KEYS.NUMBER_LIT_UP, this.number - 1)
    this.setScale(STANDARD_LARGE_NUMBER_RATIO)

    await tweenPromise(this.scene, {
      targets: this,
      scale: 1,
      x: newPosition.x,
      y: newPosition.y,
      duration: 300,
    })()
    await tweenPromise(this.scene, {
      targets: this,
      scale: 1,
      duration: 500,
    })()
    await tweenPromise(this.scene, {
      targets: this,
      scale: STANDARD_LARGE_NUMBER_RATIO,
      x: originalPosition.x,
      y: originalPosition.y,
      duration: 200,
    })()

    super.transitionToSystemSelected()
    this.setScale(1)
  }

  async transitionToSelectedHit() {
    this.glow?.setPosition(1, 1)
    const originalPosition = new Phaser.Math.Vector2(this.x, this.y)
    const newPosition = this.calculatePopPositionInFrame()

    super.transitionToSelectedHit()
    this.glow?.setVisible(false)

    for (let flicker = 1; flicker <= 3; flicker++) {
      this.image.setTexture(NEON_LIGHT_TEXTURE_KEYS.NUMBER_HIT, this.number - 1)
      this.glow?.setVisible(true)
      await tweenWaitPromise(this.scene, 100)()
      this.image.setTexture(NEON_LIGHT_TEXTURE_KEYS.NUMBER_PLAYER_SELECTED, this.number - 1)
      this.glow?.setVisible(false)
      this.setScale(1)
      await tweenWaitPromise(this.scene, 400)()
    }
    this.image.setTexture(NEON_LIGHT_TEXTURE_KEYS.NUMBER_HIT_TRANSITION, this.number - 1)
    this.image.setScale(STANDARD_LARGE_NUMBER_RATIO)

    this.glow?.setVisible(true)

    await tweenWaitPromise(this.scene, 100)()

    await tweenPromise(this.scene, {
      targets: this,
      scale: 3,
      x: newPosition.x,
      y: newPosition.y,
      duration: 300,
    })()

    await tweenPromise(this.scene, {
      targets: this,
      scale: 3,
      duration: 500,
    })()
    await tweenPromise(this.scene, {
      targets: this,
      scale: 1,
      x: originalPosition.x,
      y: originalPosition.y,
      duration: 200,
    })()
    this.image.setTexture(NEON_LIGHT_TEXTURE_KEYS.NUMBER_HIT, this.number - 1)
    this.image.setScale(1)
  }

  async skip() {
    await super.skip()
  }
}
